import type { AxiosError } from 'axios';
import { authClient } from './client';

/**
 * Backend contract:
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * ### GET /api/admin/queue-badges
 * Auth:   Bearer token (solo admin). Devolver 403 para usuarios no-admin.
 * ─────────────────────────────────────────────────────────────────────────────
 * Sin query params. Debe ser barato — idealmente dos COUNT queries en paralelo.
 *
 * Lógica:
 *   - photoQueuePending   → COUNT de variantes de inventario donde al menos
 *                           una foto requerida está pendiente Y
 *                           photo_requirements_waived = false
 *                           (equivalente: los ítems que aparecen en GET /api/admin/photo-queue)
 *   - cleaningQueuePending → COUNT de variantes donde is_clean = false
 *                           (solo productos usados — los nuevos siempre llegan limpios)
 *
 * Respuesta 200:
 * ```json
 * {
 *   "message": "OK",
 *   "data": {
 *     "photoQueuePending": 8,
 *     "cleaningQueuePending": 5
 *   }
 * }
 * ```
 *
 * Errores:
 *   - 403 → usuario no es admin
 *   - 401 → no autenticado
 */

export interface QueueBadges {
  photoQueuePending: number;
  cleaningQueuePending: number;
}

export type QueueBadgesResult = { ok: true; data: QueueBadges } | { ok: false; error: string };

export async function fetchQueueBadges(): Promise<QueueBadgesResult> {
  try {
    const { data } = await authClient.get<unknown>('/api/admin/queue-badges');
    const o = data as Record<string, unknown>;
    const inner = (o?.data !== undefined && typeof o.data === 'object' ? o.data : o) as Record<
      string,
      unknown
    >;
    const photoQueuePending = Number(inner?.photoQueuePending ?? inner?.photo_queue_pending ?? 0);
    const cleaningQueuePending = Number(
      inner?.cleaningQueuePending ?? inner?.cleaning_queue_pending ?? 0
    );
    return {
      ok: true,
      data: {
        photoQueuePending: Number.isFinite(photoQueuePending) ? photoQueuePending : 0,
        cleaningQueuePending: Number.isFinite(cleaningQueuePending) ? cleaningQueuePending : 0,
      },
    };
  } catch (e) {
    const ax = e as AxiosError<{ message?: string }>;
    const msg =
      ax.response?.data?.message ?? 'No se pudieron cargar los contadores de la barra lateral.';
    return { ok: false, error: msg };
  }
}
